import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { ApiError } from '@/lib/api/client'
import { einvoiceApi } from '@/lib/api/einvoice-api'
import { invalidateEInvoiceDocument } from '@/hooks/use-einvoice-observation'
import { m } from '@/lib/paraglide/messages'
import { invalidateRejectedEInvoiceCredential } from '@/hooks/use-einvoice-credential'

export const einvoiceKeys = {
    all: ['einvoices'] as const,
    detail: (orderId: string) =>
        [...einvoiceKeys.all, 'detail', orderId] as const,
    observation: (orderId: string) =>
        [...einvoiceKeys.all, 'observation', orderId] as const,
    document: (orderId: string) =>
        [...einvoiceKeys.all, 'document', orderId] as const,
    editor: (orderId: string) =>
        [...einvoiceKeys.all, 'editor', orderId] as const,
    misaState: (orderId: string) =>
        [...einvoiceKeys.all, 'misa-state', orderId] as const,
    usage: () => [...einvoiceKeys.all, 'usage'] as const,
}

export function useEInvoice(orderId: string, enabled = true) {
    return useQuery({
        queryKey: einvoiceKeys.detail(orderId),
        queryFn: async () => {
            try {
                const res = await einvoiceApi.get(orderId)
                return res.data ?? null
            } catch (error) {
                if (
                    error instanceof ApiError &&
                    error.status === 404 &&
                    error.code === 'EINVOICE_NOT_FOUND'
                )
                    return null
                throw error
            }
        },
        enabled,
        refetchInterval: (query) => {
            const status = query.state.data?.status
            return status &&
                [
                    'pending',
                    'created',
                    'issuing',
                    'publishing',
                    'publish_unknown',
                ].includes(status) &&
                query.state.fetchFailureCount < 10
                ? 2000
                : false
        },
        // 404 is normal during the first 1-2s after checkout (worker hasn't
        // created the row yet). Don't spam retries — the poll loop will
        // pick it up on the next tick.
        retry: (count, err) => {
            if (err instanceof ApiError && err.status === 404) return false
            return count < 3
        },
    })
}

// useCreateEInvoiceDraft saves the preview behind "Phát hành". It is never
// announced: users see no draft, only the preview that opens (M-176).
export function useCreateEInvoiceDraft() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: async (orderId: string) => {
            const res = await einvoiceApi.draft(orderId)
            return res.data
        },
        onSuccess: (data, orderId) => {
            invalidateEInvoiceDocument(qc, orderId)
            if (data) {
                qc.setQueryData(einvoiceKeys.detail(orderId), data)
            }
            void qc.invalidateQueries({
                queryKey: einvoiceKeys.detail(orderId),
            })
        },
        onError: (error) => {
            invalidateRejectedEInvoiceCredential(qc, error)
            toast.error(
                error instanceof ApiError && error.message
                    ? error.message
                    : m.einvoice_draft_failed(),
            )
        },
    })
}

export function useEInvoiceDraftEditor(orderId: string) {
    return useQuery({
        queryKey: einvoiceKeys.editor(orderId),
        queryFn: async () => (await einvoiceApi.getDraftEditor(orderId)).data,
        staleTime: 0,
        gcTime: 0,
        retry: false,
        refetchOnWindowFocus: false,
    })
}
